// Homepage "Today's Menu — {area}" strip.
//
// - Reads the delivery area selected via DeliveryAreaSelector (localStorage
//   + window event).
// - Resolves a representative plan/combo/pref/rule that covers that area
//   from the public MP2 catalog.
// - Renders today's published, area-valid dishes via
//   getPublicUpcomingMenu(days=1). All resolution and eligibility
//   filtering happens server-side.
// - Add-to-cart is disabled/inert; cart & authoritative per-dish pricing
//   are Phase 3.
// - Clean empty state when no area is picked or nothing is published yet.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import { useSelectedDeliveryAreaId } from "./DeliveryAreaSelector";
import {
  getPublicUpcomingMenu,
  type UpcomingMenu,
  type MenuDish,
} from "@/lib/public/meal-plans-v2-menu.functions";
import { todayInIST } from "@/lib/date/ist";
import { useAlacarteCart } from "@/hooks/useAlacarteCart";
import { CheckCircle2 } from "lucide-react";

const SLOT_ORDER = ["breakfast", "lunch", "snacks", "dinner"];

export function TodaysMenu() {
  const { catalog, isMp2Visible } = usePublicMp2Catalog();
  const areaId = useSelectedDeliveryAreaId();
  const fn = useServerFn(getPublicUpcomingMenu);

  const seed = useMemo(() => {
    if (!catalog || !areaId) return null;
    // Find any rule covering the selected area.
    const ruleIdsForArea = new Set(
      catalog.rule_areas.filter((ra) => ra.area_id === areaId).map((ra) => ra.rule_id),
    );
    if (ruleIdsForArea.size === 0) return null;
    // Prefer a non-health-review plan for the homepage strip.
    const plan =
      catalog.plans.find((p) => {
        if (p.health_review_required) return false;
        const allowed = new Set(p.allowed_delivery_rule_ids);
        return (
          allowed.size === 0 ||
          [...ruleIdsForArea].some((rid) => allowed.has(rid))
        );
      }) ?? catalog.plans[0];
    if (!plan) return null;
    const rule =
      catalog.delivery_rules.find((r) => {
        if (!ruleIdsForArea.has(r.id)) return false;
        const allow = plan.allowed_delivery_rule_ids;
        return allow.length === 0 || allow.includes(r.id);
      }) ?? catalog.delivery_rules.find((r) => ruleIdsForArea.has(r.id));
    const combo =
      catalog.combinations.find((c) =>
        plan.allowed_combination_ids.length === 0 ||
        plan.allowed_combination_ids.includes(c.id),
      ) ?? catalog.combinations[0];
    const pref =
      catalog.food_preferences.find((p) =>
        plan.allowed_preference_codes.length === 0 ||
        plan.allowed_preference_codes.includes(p.code),
      ) ?? catalog.food_preferences[0];
    const area = catalog.delivery_areas.find((a) => a.id === areaId);
    if (!rule || !combo || !pref || !area) return null;
    return { plan, combo, pref, rule, area };
  }, [catalog, areaId]);

  const q = useQuery({
    queryKey: ["mp2-today-menu", seed?.plan.id ?? "", seed?.area.id ?? "", todayInIST()],
    enabled: Boolean(seed),
    queryFn: () =>
      fn({
        data: {
          plan_id: seed!.plan.id,
          combination_id: seed!.combo.id,
          food_preference_code: seed!.pref.code,
          delivery_rule_id: seed!.rule.id,
          delivery_area_id: seed!.area.id,
          start_date: todayInIST(),
          days: 1,
        },
      }),
  });

  if (!isMp2Visible) return null;

  return (
    <Section>
      <div className="max-w-2xl">
        <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Today's Menu{seed ? ` — ${seed.area.neighbourhood}` : ""}
        </span>
        <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">
          What's cooking today.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Fresh dishes prepared to slot and delivered to your doorstep. Pick your
          neighbourhood above to see the exact menu we're serving today.
        </p>
      </div>

      <div className="mt-10">
        {!areaId ? (
          <Alert>
            <AlertTitle>Select a delivery area</AlertTitle>
            <AlertDescription>
              Choose your neighbourhood above to see today's menu tailored to
              your delivery route.
            </AlertDescription>
          </Alert>
        ) : !seed ? (
          <Alert>
            <AlertTitle>We're not delivering to that area yet</AlertTitle>
            <AlertDescription>
              We're expanding fast — please try a nearby neighbourhood or reach
              out via the Contact page.
            </AlertDescription>
          </Alert>
        ) : q.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading today's menu…</div>
        ) : (
          <TodayGrid data={q.data} />
        )}
      </div>

      <div className="mt-8 flex justify-center">
        <Button asChild variant="outline">
          <Link to="/weekly-menu" search={{}}>
            View this week's full menu
          </Link>
        </Button>
      </div>
    </Section>
  );
}

function TodayGrid({ data }: { data: UpcomingMenu | { error: string } | undefined }) {
  if (!data || "error" in (data as { error?: string })) {
    return (
      <Alert>
        <AlertTitle>Today's menu isn't published yet</AlertTitle>
        <AlertDescription>
          Our team publishes menus a short window ahead. Please check back soon
          or configure a meal plan to see upcoming days.
        </AlertDescription>
      </Alert>
    );
  }
  const menu = data as UpcomingMenu;
  const day = menu.days?.[0];
  if (!day) {
    return (
      <Alert>
        <AlertTitle>Today's menu isn't published yet</AlertTitle>
        <AlertDescription>
          We publish daily menus in advance. Please check back later today or
          tomorrow.
        </AlertDescription>
      </Alert>
    );
  }
  const slots = SLOT_ORDER.map((s) => day.slots[s]?.primary).filter(
    (d): d is NonNullable<typeof d> => Boolean(d),
  );
  if (slots.length === 0) {
    return (
      <Alert>
        <AlertTitle>No dishes published for today</AlertTitle>
        <AlertDescription>
          Our menu will refresh shortly. Meanwhile, explore our meal plans to
          preview upcoming weeks.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {slots.map((d) => (
        <article
          key={d.id}
          className="flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card"
        >
          {d.image_url ? (
            <div className="aspect-[4/3] overflow-hidden bg-secondary/40">
              <img
                src={d.image_url}
                alt={d.name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="aspect-[4/3] bg-secondary/40" aria-hidden />
          )}
          <div className="flex flex-1 flex-col p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {d.meal_slot}
            </div>
            <div className="mt-1 line-clamp-2 font-display text-lg font-medium">
              {d.name}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {d.is_veg ? (
                <Badge variant="secondary" className="text-[10px]">Veg</Badge>
              ) : d.is_veg === false ? (
                <Badge variant="outline" className="text-[10px]">Non-veg</Badge>
              ) : null}
              {d.calories_kcal != null ? (
                <Badge variant="outline" className="text-[10px]">
                  {Math.round(Number(d.calories_kcal))} kcal
                </Badge>
              ) : null}
            </div>
            <div className="mt-4 flex-1" />
            <AddToCartButton dish={d} />
          </div>
        </article>
      ))}
    </div>
  );
}

function AddToCartButton({ dish }: { dish: any }) {
  const { addItem, isSubmitting } = useAlacarteCart() as any;
  const [added, setAdded] = useState(false);

  const handleAdd = async () => {
    try {
      await addItem(dish.id, dish.meal_slot as any, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch (err) {
      console.error("Failed to add to cart", err);
    }
  };

  return (
    <Button
      type="button"
      variant={added ? "secondary" : "outline"}
      size="sm"
      disabled={isSubmitting || added}
      className="mt-3 w-full"
      onClick={handleAdd}
    >
      {added ? (
        <span className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Added
        </span>
      ) : (
        `Add to cart · ₹${(Number(dish.unit_price_paise || 0) / 100).toFixed(0)}`
      )}
    </Button>
  );
}